import { useEffect, useState, useRef, memo } from 'react'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import { useQuery } from '@apollo/client/react'
import cn from 'classnames'
// Utils
import styles from './NationWide.module.scss'

import { useMediaQuery } from '@utils'
import { useFavourites } from '@hooks'
import { ISearch, SearchItemType } from '@types'
import { PUPPY_SEARCH } from '@graphql'

const PuppyCard = dynamic(() => import('@/components/newPuppyCard'))
const SignupModal = dynamic(() => import('@/components/signupModal'))

type NationWideProps = {
  hasBreeds: boolean
  title: string
  allDogs: SearchItemType[]
  slug: string
}

const NationWide = memo(({ hasBreeds, title, allDogs, slug }: NationWideProps) => {
  const ref = useRef(null)
  const isMobile = useMediaQuery('(max-width:600px)')
  const {
    selectedDog,
    toggleFavourite,
    isFavourite: isFavouriteHook,
    refreshUserData,
  } = useFavourites()

  const [dogs, setDogs] = useState<SearchItemType[]>(allDogs)
  const [showButtons, setShowButtons] = useState(false)
  const [autosize, setAutosize] = useState(false)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const [showSignup, setShowSignup] = useState(false)
  const [page, setPage] = useState(1)

  const { data: _allPuppies } = useQuery<ISearch>(PUPPY_SEARCH, {
    variables: { breedSlug: slug || '', page, limit: 2 },
  })

  useEffect(() => {
    if (allDogs.length > 3) setShowButtons(true)
  }, [showButtons])

  useEffect(() => {
    if (_allPuppies?.search?.rows?.length) setDogs([...dogs, ...(_allPuppies?.search?.rows ?? [])])
  }, [page])

  useEffect(() => setAutosize(isMobile), [isMobile])

  /* Swiper Handlers   */
  const goNext = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slideNext()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
      setPage(page + 1)
    }
  }

  const goPrev = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slidePrev()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
    }
  }

  if (allDogs.length === 0) {
    return null
  }

  return (
    <>
      <SignupModal
        isOpen={showSignup}
        close={() => setShowSignup(false)}
        success={() => {
          setShowSignup(false)
          refreshUserData()
          toggleFavourite(selectedDog)
        }}
      />
      <div className={styles.nationWideContainer}>
        <div className={styles.puppiesNationWideContainer}>
          <div className={styles.puppiesTitle}>
            <img
              src="/travel-on.svg"
              alt="Plane icon with a shield that has a heart icon inside it"
              className={styles.travel}
            />{' '}
            {slug !== undefined ? (
              <Link href={`/puppies-for-sale/${slug}`} className={styles.title}>
                <h2>{title}</h2>
              </Link>
            ) : (
              <h2>{title}</h2>
            )}
            {!isMobile ? (
              hasBreeds ? (
                <Link
                  href={
                    slug
                      ? `${process.env.NEXT_PUBLIC_ROOT_URL}/search?breed=${slug}`
                      : `${process.env.NEXT_PUBLIC_ROOT_URL}/search`
                  }
                  className={styles.btn}
                >
                  <button>View all</button>
                </Link>
              ) : (
                <Link href="/search" className={styles.btn}>
                  <button>View all</button>
                </Link>
              )
            ) : null}
          </div>
          {/* desktop swiper */}
          <div className={cn(styles.featuredPuppies, styles.desktop)}>
            {showButtons && (
              <>
                <button
                  className={cn({
                    [styles.swiperButton]: true,
                    [styles.swiperPrev]: !isBeginning,
                    [styles.hidePrev]: isBeginning,
                  })}
                  onClick={goPrev}
                />
                <button
                  className={cn({
                    [styles.swiperButton]: true,
                    [styles.swiperNext]: !isEnd,
                    [styles.hideNext]: isEnd,
                  })}
                  onClick={goNext}
                />
              </>
            )}
            <Swiper
              speed={1000}
              pagination={{ clickable: true }}
              initialSlide={0}
              grabCursor
              breakpoints={{
                1020: {
                  slidesPerView: 4,
                  spaceBetween: 20,
                },
                860: {
                  slidesPerView: 4,
                  spaceBetween: 20,
                },
                540: {
                  slidesPerView: 3,
                  spaceBetween: 20,
                },
              }}
              onSlideChange={() => setPage(page + 1)}
              ref={ref}
            >
              {dogs?.map((d, i) => (
                <SwiperSlide key={i}>
                  <div className={styles.cardContainer}>
                    <PuppyCard
                      dog={d}
                      isFavourite={isFavouriteHook(d.id)}
                      updateFavourite={() => {
                        const result = toggleFavourite(d.id)
                        if (!result) {
                          setShowSignup(true)
                        }
                      }}
                      autosize={autosize}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
          {/* mobile swiper */}
          {isMobile && (
            <div className={cn(styles.featuredPuppies, styles.mobile)}>
              <Swiper
                speed={1000}
                initialSlide={0}
                grabCursor
                breakpoints={{
                  1020: {
                    slidesPerView: 4,
                    spaceBetween: 20,
                  },
                  860: {
                    slidesPerView: 4,
                    spaceBetween: 20,
                  },
                  540: {
                    slidesPerView: 3,
                    spaceBetween: 20,
                  },
                  360: {
                    slidesPerView: 'auto',
                    spaceBetween: 10,
                  },
                }}
                onSlideChange={() => setPage(page + 1)}
              >
                {dogs?.map((d, i) => (
                  <SwiperSlide key={i}>
                    <div className={styles.cardContainer}>
                      <PuppyCard
                        dog={d}
                        isFavourite={isFavouriteHook(d.id)}
                        updateFavourite={() => {
                          const result = toggleFavourite(d.id)
                          if (!result) {
                            setShowSignup(true)
                          }
                        }}
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}

          {isMobile &&
            (hasBreeds ? (
              <Link
                href={
                  slug
                    ? `${process.env.NEXT_PUBLIC_ROOT_URL}/search?breed=${slug}`
                    : `${process.env.NEXT_PUBLIC_ROOT_URL}/search`
                }
                className={styles.btnMobile}
              >
                <button>
                  View all
                  <div className={styles.arrow} />
                </button>
              </Link>
            ) : (
              <Link href="/search" className={styles.btnMobile}>
                <button>
                  View all
                  <div className={styles.arrow} />
                </button>
              </Link>
            ))}
        </div>
        {/* MOBILE */}

        <div className={styles.bulletsContainerMobile}>
          <section className={styles.btn}>
            <Link href="/puppy-travel">
              <button>Learn more</button>
            </Link>
          </section>
          <div className={styles.bulletsBox}>
            <Swiper
              modules={[Autoplay]}
              initialSlide={0}
              grabCursor
              autoplay={{
                delay: 5000,
                disableOnInteraction: true,
              }}
              slidesPerView={1}
              centeredSlides
              spaceBetween={30}
              loop
              pagination={{
                clickable: true,
              }}
            >
              <SwiperSlide>
                <div className={styles.bulletFact}>
                  <img src="/landing/state/bullet.svg" alt="bullet" />
                  <p>
                    <strong>70% of our puppies</strong> arrive at their new homes{' '}
                    <strong>using travel</strong>.
                  </p>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className={styles.bulletFact}>
                  <img src="/landing/state/bullet.svg" alt="bullet" />
                  <p>
                    Our team will find you the <strong>most affordable</strong> travel option
                  </p>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className={styles.bulletFact}>
                  <img src="/landing/state/bullet.svg" alt="bullet" />
                  <p>
                    All travel is provided by an insured <strong>registered provider</strong>
                  </p>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>
        </div>

        {/* DESKTOP */}
        <div className={styles.bulletsContainerDesktop}>
          <div className={styles.bulletsBox}>
            <section className={styles.items}>
              <div className={styles.bulletFact}>
                <img src="/landing/state/bullet.svg" alt="bullet" />
                <p>
                  <strong>70% of our puppies</strong> arrive at their new homes{' '}
                  <strong>using travel</strong>.
                </p>
              </div>
              <div className={styles.bulletFact}>
                <img src="/landing/state/bullet.svg" alt="bullet" />
                <p>
                  Our team will find you the <strong>most affordable</strong> travel option
                </p>
              </div>
              <div className={styles.bulletFact}>
                <img src="/landing/state/bullet.svg" alt="bullet" />
                <p>
                  All travel is provided by an insured <strong>registered provider</strong>
                </p>
              </div>
            </section>
            <section className={styles.LearnMoreDesktop}>
              <Link href="/puppy-travel">
                <button>Learn more</button>
              </Link>
            </section>
          </div>
        </div>
      </div>
    </>
  )
})

export default NationWide
